/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import FlagProvider from "@unleash/proxy-client-react";
import App from "./App";
import { Provider } from "react-redux";
import store from "./config/store";
import {
  AppLoader,
  initializeSentry,
  MeddreamWindow,
  MeddreamWindowContext,
  CaptchaProviderContext,
} from "@advapacs/common";
import setupAxiosInterceptors from "./config/axios-interceptor";
import { setUpAxiosInstance } from "./config/axios-region";
import UnleashContext from "./context/UnleashContext";
import Unleash from "./context/Unleash";
import { formatRegionUrl } from "./utils/utils";
import { MatomoProvider } from "@datapunt/matomo-tracker-react";
import initializeMatomoTrackerInstance from "./matomoTracker";
import { MatomoInstance } from "@datapunt/matomo-tracker-react/lib/types";
import { resolveBrandUrl } from "@src/pages/login/ducks/operations";
import { BrandProvider } from "@src/pages/login/BrandContext";
import { initializeFaro } from "./services/initializeFaro";

const router = createBrowserRouter([
  {
    path: "/*",
    element: <App />,
  },
]);
async function bootstrap() {
  const brand = await resolveBrandUrl();
  setupAxiosInterceptors(store, brand);
  setUpAxiosInstance(
    store,
    formatRegionUrl(
      localStorage.getItem("region"),
      localStorage.getItem("logged") === "true" || false
    )
  );
  initializeFaro();
  initializeSentry();
  const root = ReactDOM.createRoot(
    document.getElementById("root") as HTMLElement
  );

  root.render(
    <React.StrictMode>
      <CaptchaProviderContext>
        <React.Suspense fallback={<AppLoader.Loader />}>
          <MeddreamWindowContext.Provider value={MeddreamWindow}>
            <Provider store={store}>
              <UnleashContext.Provider value={Unleash}>
                <FlagProvider config={Unleash.getConfig()}>
                  {/* @ts-ignore */}
                  <MatomoProvider
                    value={
                      initializeMatomoTrackerInstance as unknown as MatomoInstance
                    }
                  >
                    <BrandProvider initialBrand={brand}>
                      <RouterProvider router={router} />
                    </BrandProvider>
                  </MatomoProvider>
                </FlagProvider>
              </UnleashContext.Provider>
            </Provider>
          </MeddreamWindowContext.Provider>
        </React.Suspense>
      </CaptchaProviderContext>
    </React.StrictMode>
  );
}

(async () => {
  try {
    await bootstrap();
  } catch (err) {
    console.error("Bootstrap failed:", err);
    const root = ReactDOM.createRoot(
      document.getElementById("root") as HTMLElement
    );
    setupAxiosInterceptors(store, "");
    setUpAxiosInstance(
      store,
      formatRegionUrl(
        // @ts-ignore
        localStorage.getItem("region") || null,
        localStorage.getItem("logged") === "true" || false
      )
    );

    initializeSentry();

    root.render(
      <React.StrictMode>
        <CaptchaProviderContext>
          <React.Suspense fallback={<AppLoader.Loader />}>
            <MeddreamWindowContext.Provider value={MeddreamWindow}>
              <Provider store={store}>
                <UnleashContext.Provider value={Unleash}>
                  <FlagProvider config={Unleash.getConfig()}>
                    {/* @ts-ignore */}
                    <MatomoProvider
                      value={
                        initializeMatomoTrackerInstance as unknown as MatomoInstance
                      }
                    >
                      <RouterProvider router={router} />
                    </MatomoProvider>
                  </FlagProvider>
                </UnleashContext.Provider>
              </Provider>
            </MeddreamWindowContext.Provider>
          </React.Suspense>
        </CaptchaProviderContext>
      </React.StrictMode>
    );
  }
})();
